import * as Sentry from "@sentry/vue";
import {
  syncMonitoringScopeFromStorage,
  trackMonitoringEvent,
} from "~/utils/monitoring";

const clampSampleRate = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return fallback;
  }

  return Math.min(1, Math.max(0, parsed));
};

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const router = useRouter();
  const dsn = config.public.glitchtipDsn;

  if (!dsn) {
    return;
  }

  Sentry.init({
    app: nuxtApp.vueApp,
    dsn,
    environment: config.public.glitchtipEnvironment,
    release: config.public.appRelease || undefined,
    sampleRate: clampSampleRate(config.public.glitchtipErrorSampleRate, 1),
    tracesSampleRate: clampSampleRate(
      config.public.glitchtipTraceSampleRate,
      import.meta.dev ? 1 : 0.1,
    ),
    autoSessionTracking: false,
    ignoreErrors: [
      // 微信内置浏览器自身 JSBridge 在页面离开/切后台时的时序问题（尤其 iOS beta），与本应用代码无关
      /weixinPostMessageHandlers/,
      // Nuxt 后台每小时检查新版本时，若期间发布了新部署，旧版本的 manifest 文件已被覆盖导致该请求失败；不影响当前页面使用
      /malformed app manifest/,
    ],
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        routeLabel: "path",
      }),
    ],
    initialScope: (scope) => {
      scope.setTag("app_name", "evaluation-web");
      scope.setTag("app_type", "nuxt-spa");
      return scope;
    },
  });

  syncMonitoringScopeFromStorage();
  trackMonitoringEvent("monitoring_initialized", {
    route: router.currentRoute.value.fullPath,
  });

  router.afterEach((to, from) => {
    syncMonitoringScopeFromStorage();
    Sentry.addBreadcrumb({
      category: "navigation",
      type: "navigation",
      level: "info",
      message: to.fullPath,
      data: {
        from: from.fullPath,
      },
    });
  });

  nuxtApp.hook("page:finish", () => {
    syncMonitoringScopeFromStorage();
  });
});
